// src/components/books/GradingSidebar.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  ChevronRight,
  ChevronDown,
  User,
  Award,
  Clock,
  MessageSquare
} from 'lucide-react';
import { MathRenderer } from '../math/MathRenderer';
import { flipbookService } from '../../services/flipbookService';

export const GradingSidebar = ({ 
  bookId, 
  bookletId, 
  bookletTitle, 
  pageNumber,
  onClose 
}) => {
  const [questions, setQuestions] = useState([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [responses, setResponses] = useState([]);
  const [modelAnswers, setModelAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedResponses, setExpandedResponses] = useState({});
  const [stepMarkings, setStepMarkings] = useState({});
  const [feedback, setFeedback] = useState({});
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [manualScores, setManualScores] = useState({});
  const [useManualScore, setUseManualScore] = useState({});

  useEffect(() => {
    loadData();
  }, [bookId, bookletId, pageNumber]);

  useEffect(() => {
    if (selectedQuestionId) {
        loadResponsesForQuestion(selectedQuestionId);
    } else {
        setViewMode('list');
    }
  }, [selectedQuestionId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const loadedQuestions = await flipbookService.getPageQuestions(bookId, bookletId, pageNumber);
      setQuestions(loadedQuestions);
      
      if (loadedQuestions.length > 0 && !selectedQuestionId) {
        setSelectedQuestionId(loadedQuestions[0].id);
      }
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadResponsesForQuestion = async (questionId) => {
    try {
        const allResponses = await flipbookService.getPageResponses(bookId, bookletId, pageNumber);
        
        const models = allResponses.filter(r => 
        r.responseType === 'model' && 
        r.questionId === questionId
        );
        
        const studentResponses = allResponses.filter(r => 
        r.responseType !== 'model' && 
        r.questionId === questionId
        );
        
        setModelAnswers(models);
        setResponses(studentResponses);

        const initialMarkings = {};
        const initialManualScores = {};
        const initialUseManual = {};
        const initialFeedback = {};
        
        studentResponses.forEach(response => {
        if (response.grading?.stepMarkings) {
            initialMarkings[response.id] = response.grading.stepMarkings;
        }
        if (response.grading?.feedback) {
            initialFeedback[response.id] = response.grading.feedback;
        }
        if (response.grading?.score?.manual) {
            initialManualScores[response.id] = response.grading.score.percentage;
            initialUseManual[response.id] = true;
        }
        });
        
        setStepMarkings(initialMarkings);
        setManualScores(initialManualScores);
        setUseManualScore(initialUseManual);
        setFeedback(initialFeedback);

    } catch (error) {
        console.error('Error loading responses:', error);
    }
  };

  const parseContentIntoSteps = (content) => {
    if (!content) return [];
    
    const lines = content.split('\n').filter(line => line.trim());
    return lines.map((line, index) => ({
      id: index,
      content: line.trim()
    }));
  };

  const toggleStepMarking = (responseId, stepId) => {
    setStepMarkings(prev => {
      const current = prev[responseId] || {};
      const currentStatus = current[stepId];
      
      let newStatus;
      if (currentStatus === 'correct') {
        newStatus = 'incorrect';
      } else if (currentStatus === 'incorrect') {
        newStatus = undefined;
      } else {
        newStatus = 'correct';
      }
      
      const updated = { ...current };
      if (newStatus) {
        updated[stepId] = newStatus;
      } else {
        delete updated[stepId];
      }
      
      return { ...prev, [responseId]: updated };
    });
  };

  const calculateScore = (responseId) => {
    const response = responses.find(r => r.id === responseId);
    if (!response) return null;

    const question = questions.find(q => q.id === response.questionId);
    const maxMarks = question?.marks || 10;

    // Check if manual score is being used
    if (useManualScore[responseId] && manualScores[responseId] !== undefined) {
        return {
        manual: true,
        score: manualScores[responseId],
        maxMarks: maxMarks,
        percentage: Math.round((manualScores[responseId] / maxMarks) * 100)
        };
    }
    
    const markings = stepMarkings[responseId] || {};
    const steps = parseContentIntoSteps(response.content);
    const totalSteps = steps.length;
    
    if (totalSteps === 0) return null;
    
    const correctSteps = Object.values(markings).filter(m => m === 'correct').length;
    const percentage = Math.round((correctSteps / totalSteps) * 100);
    const calculatedScore = Math.round((percentage / 100) * maxMarks * 10) / 10; // Round to 1 decimal
    
    return {
        manual: false,
        correct: correctSteps,
        total: totalSteps,
        score: calculatedScore,
        maxMarks: maxMarks,
        percentage: percentage
    };
    };


  const saveGrading = async (responseId) => {
    setSaving(true);
    try {
      const score = calculateScore(responseId);
      
      await flipbookService.updatePageResponse(bookId, responseId, {
        grading: {
          stepMarkings: stepMarkings[responseId] || {},
          feedback: feedback[responseId] || '',
          score: score,
          gradedAt: new Date().toISOString()
        }
      });
      
      alert('Grading saved successfully!');
    } catch (error) {
      console.error('Error saving grading:', error);
      alert('Failed to save grading');
    } finally {
      setSaving(false);
    }
  };

  const toggleResponseExpansion = (responseId) => {
    setExpandedResponses(prev => ({
      ...prev,
      [responseId]: !prev[responseId]
    }));
  };

  const getResponseTypeColor = (type) => {
    switch (type) {
      case 'practice': return 'bg-blue-100 text-blue-700';
      case 'graded': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="fixed right-0 top-0 h-full w-120 bg-white shadow-2xl border-l border-gray-200 z-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="fixed right-0 top-0 h-screen w-[600px] bg-white shadow-2xl border-l border-gray-200 z-50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-400 bg-gray-100">
        <div>
            <h3 className="text-lg font-bold text-gray-900">Grading Center</h3>
            <p className="text-sm text-gray-600">{bookletTitle}</p>
            <p className="text-xs text-gray-500">Page {pageNumber + 1}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-white/20 rounded-lg transition-colors text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Questions List */}
        {viewMode === 'list' ? (
        <div className="border-b border-gray-200 bg-gray-50 w-[600px] p-3 overflow-y-auto max-h-64">
            <p className="text-xs font-semibold text-gray-600 mb-2 uppercase">Questions ({questions.length})</p>
            <div className="space-y-2">
            {questions.map((q, index) => (
                <button
                key={q.id}
                onClick={() => {
                    setSelectedQuestionId(q.id);
                    setViewMode('detail');
                }}
                className="w-full text-left p-3 rounded-lg transition-all bg-white text-gray-700 hover:bg-gray-100"
                >
                <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-bold">Q{index + 1}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                    {responses.filter(r => r.questionId === q.id).length} responses
                    </span>
                </div>
                <div className="text-xs line-clamp-2">
                    <MathRenderer content={q.questionText} />
                </div>
                </button>
            ))}
            </div>
        </div>
        ) : (
        <div className="border-b border-gray-200 bg-blue-50 p-3">
            <button
            onClick={() => setViewMode('list')}
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium mb-2"
            >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to Questions
            </button>
            {selectedQuestionId && (
            <div className="bg-white rounded-lg p-3 border border-blue-200">
                <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-gray-600">
                    Q{questions.findIndex(q => q.id === selectedQuestionId) + 1}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                    {responses.length} responses
                </span>
                </div>
                <div className="text-sm font-medium text-gray-900">
                <MathRenderer content={questions.find(q => q.id === selectedQuestionId)?.questionText || ''} />
                </div>
            </div>
            )}
        </div>
        )}

      {/* Responses Section */}
      <div className="flex-1 w-[600px] overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        {selectedQuestionId && (
          <>
            {/* Model Answers */}
            {modelAnswers.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="w-4 h-4 text-green-600" />
                  <h4 className="text-sm font-bold text-gray-900">Model Answers ({modelAnswers.length})</h4>
                </div>
                <div className="space-y-3">
                  {modelAnswers.map((answer) => (
                    <div key={answer.id} className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-3 h-3 text-green-600" />
                        <span className="text-xs font-medium text-green-800">
                          {answer.userDisplayName}
                        </span>
                      </div>
                      <div className="text-sm text-gray-800 prose prose-sm max-w-none">
                        <MathRenderer content={answer.content} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Student Responses */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-blue-600" />
                <h4 className="text-sm font-bold text-gray-900">Student Responses ({responses.length})</h4>
              </div>

              {responses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm">No responses yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {responses.map((response) => {
                    const isExpanded = expandedResponses[response.id];
                    const steps = parseContentIntoSteps(response.content);
                    const score = calculateScore(response.id);
                    
                    return (
                      <div key={response.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        {/* Response Header */}
                        <div 
                          className="p-3 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => toggleResponseExpansion(response.id)}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-600" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-gray-600" />
                              )}
                              <span className="text-sm font-semibold text-gray-900">
                                {response.userDisplayName}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${getResponseTypeColor(response.responseType)}`}>
                                {response.responseType}
                              </span>
                            </div>
                            {score && (
                              <div className={`text-xs font-bold px-2 py-1 rounded ${
                                score.percentage >= 70 ? 'bg-green-100 text-green-700' :
                                score.percentage >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {score.percentage}%
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {new Date(response.createdAt?.toDate?.() || response.createdAt).toLocaleDateString()}
                            </div>
                            {score && (
                              <span>{score.correct}/{score.total} steps</span>
                            )}
                          </div>
                        </div>

                        {/* Response Content - Grading */}
                        {isExpanded && (
                          <div className="p-4 space-y-4">
                            {/* Steps Grading */}
                            <div className="space-y-2">
                              <p className="text-xs font-semibold text-gray-600 uppercase mb-2">
                                Mark Steps
                              </p>
                              {steps.map((step) => {
                                const marking = stepMarkings[response.id]?.[step.id];
                                
                                return (
                                  <div
                                    key={step.id}
                                    onClick={() => toggleStepMarking(response.id, step.id)}
                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                                      marking === 'correct' 
                                        ? 'border-green-500 bg-green-50' 
                                        : marking === 'incorrect'
                                        ? 'border-red-500 bg-red-50'
                                        : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-start gap-2">
                                      <div className="flex-shrink-0 mt-0.5">
                                        {marking === 'correct' ? (
                                          <CheckCircle className="w-5 h-5 text-green-600 fill-current" />
                                        ) : marking === 'incorrect' ? (
                                          <XCircle className="w-5 h-5 text-red-600 fill-current" />
                                        ) : (
                                          <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                                        )}
                                      </div>
                                      <div className="flex-1 text-sm text-gray-800">
                                        <MathRenderer content={step.content} />
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Score Section */}
                            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-semibold text-gray-600 uppercase">
                                Score
                                </label>
                                <button
                                onClick={() => setUseManualScore(prev => ({
                                    ...prev,
                                    [response.id]: !prev[response.id]
                                }))}
                                className={`text-xs px-2 py-1 rounded transition-colors ${
                                    useManualScore[response.id]
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                }`}
                                >
                                {useManualScore[response.id] ? 'Manual' : 'Auto'}
                                </button>
                            </div>

                            {useManualScore[response.id] ? (
                                <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    max={score?.maxMarks || 10}
                                    step="0.5"
                                    value={manualScores[response.id] || ''}
                                    onChange={(e) => {
                                    const maxMarks = score?.maxMarks || 10;
                                    const value = Math.min(maxMarks, Math.max(0, parseFloat(e.target.value) || 0));
                                    setManualScores(prev => ({
                                        ...prev,
                                        [response.id]: value
                                    }));
                                    }}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    placeholder="Enter score"
                                />
                                <span className="text-sm font-medium text-gray-600">/ {score?.maxMarks || 10}</span>
                                </div>
                            ) : (
                                <div className="flex items-center justify-between">
                                <div className="text-sm text-gray-600">
                                    {score ? (
                                    <>
                                        <span className="font-medium">{score.correct}</span>
                                        <span className="text-gray-400"> / </span>
                                        <span>{score.total} steps</span>
                                    </>
                                    ) : (
                                    <span className="text-gray-400">Mark steps to calculate</span>
                                    )}
                                </div>
                                {score && (
                                    <div className="flex items-center gap-2">
                                    <div className="text-right">
                                        <div className="text-lg font-bold text-gray-900">
                                        {score.score} / {score.maxMarks}
                                        </div>
                                        <div className={`text-xs font-medium ${
                                        score.percentage >= 70 ? 'text-green-600' :
                                        score.percentage >= 50 ? 'text-yellow-600' :
                                        'text-red-600'
                                        }`}>
                                        {score.percentage}%
                                        </div>
                                    </div>
                                    </div>
                                )}
                                </div>
                            )}
                            </div>

                            {score && (
                            <div className="text-right">
                                <div className={`text-xs font-bold px-2 py-1 rounded ${
                                score.percentage >= 70 ? 'bg-green-100 text-green-700' :
                                score.percentage >= 50 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                                }`}>
                                {score.score}/{score.maxMarks}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                {score.percentage}%
                                </div>
                            </div>
                            )}

                            {/* Feedback */}
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                                <MessageSquare className="w-3 h-3 inline mr-1" />
                                Feedback
                              </label>
                              <textarea
                                value={feedback[response.id] || ''}
                                onChange={(e) => setFeedback(prev => ({
                                  ...prev,
                                  [response.id]: e.target.value
                                }))}
                                placeholder="Add feedback for the student..."
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                                rows={3}
                              />
                            </div>

                            {/* Save Button */}
                            <button
                              onClick={() => saveGrading(response.id)}
                              disabled={saving}
                              className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                            >
                              {saving ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  Saving...
                                </>
                              ) : (
                                'Save Grading'
                              )}
                            </button>

                            {/* Attachments */}
                            {response.attachments && response.attachments.length > 0 && (
                              <div className="pt-3 border-t border-gray-200">
                                <p className="text-xs font-semibold text-gray-600 uppercase mb-2">
                                  Attachments
                                </p>
                                <div className="space-y-2">
                                  {response.attachments.map((file, idx) => (
                                    <a
                                      key={idx}
                                      href={file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                                    >
                                      <span className="text-xs text-blue-600 hover:underline truncate">
                                        {file.name}
                                      </span>
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};